import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { getPerPageRecords, getPermissionData, getUserData } from "../../Actions/common";
import { deleteMultipleLogsAction, exportSystemLogDataAction, getModulesOptionsActions, logsFilterAction } from "../../Actions/LogsAction";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { DEFAULT_PAGE_SIZE, ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import ExportDataModal from "../ExportDataModal";
import renderStatusBadge from "../../utils/badges";
import showConfirmation from "../../utils/Conformation";
import { getUsersAction } from "../../Actions/UserActions";
import DataTable from "../../components/DataTable/DataTable";

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);

  return { from: start, to: end };
};

const LogsSection = () => {
  const [logsData, setLogsData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const ITEMS_PER_PAGE = localStorage.getItem("GLOBAL_SETTING") ? getPerPageRecords() : { value: DEFAULT_PAGE_SIZE };
  const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [userOperationFilter, setUserOperationFilter] = useState("all");
  const [moduleId, setModuleId] = useState("all");
  const [userNameFilter, setUserNameFilter] = useState(0);
  const [usersDataForDropdown, setUsersDataForDropdown] = useState([]);
  const [startDate, setStartDate] = useState(getDefaultDateRange().from.toISOString());
  const [endDate, setEndDate] = useState(getDefaultDateRange().to.toISOString());
  const [selectedRange, setSelectedRange] = useState(getDefaultDateRange());
  const [openExportModal, setOpenExportModal] = useState(false);
  const [selectedDataForExport, setSelectedDataForExport] = useState([]);
  const [exportData, setExportData] = useState({});
  const [moduleOptions, setModuleOptions] = useState([]);

  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  const [filtersApplied, setFiltersApplied] = useState(false);

  const [isUsersFetched, setIsUsersFetched] = useState(false);

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`) ? getPermissionData(`${userData?.id}_permissionData`) : [];
  const hasPermission = permissionData[0]?.role_permissions?.find((m) => m?.module && m?.module?.slug === "audit_trail");

  const fetchAllModulesOptions = async (isFilterAction = false) => {
    try {
      const response = await getModulesOptionsActions();
      const { data = null } = response?.data || {};
      if (data) {
        setModuleOptions(data);
      }
      if (isFilterAction) {
        setFilterButtonDisabled(true);
        setResetButtonDisabled(false);
        setFiltersApplied(true);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Something went wrong while fetching the modules list",
        severity: "error",
      });
      console.error("Error while fetching the module options ->", error);
    }
  };

  const fetchAllUsers = async () => {
    await getUsersAction(null, null, null, null, "1")
      .then((res) => {
        const { data } = res.data;
        setUsersDataForDropdown(data);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Error fetching users",
          severity: "error",
        });
      });
  };

  const fetchFilteredLogsData = useCallback( async () => { 
    setLoading(true);
    await logsFilterAction(searchValue, currentPage, selectedRowsPerPage, typeFilter, userOperationFilter, moduleId, startDate, endDate, userNameFilter)
      .then((res) => {
        const { count, rows } = res.data.data;
        setLogsData(rows);
        setTotalRecords(count);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Error fetching logs",
          severity: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  },[searchValue, currentPage, selectedRowsPerPage, typeFilter, userOperationFilter, moduleId, startDate, endDate, userNameFilter]);

  useEffect(() => {
    fetchAllModulesOptions();
  }, []);

  useEffect(() => {
    if (!isUsersFetched) {
      setIsUsersFetched(true);
      fetchAllUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    fetchFilteredLogsData();
  }, [
    searchValue,
    currentPage,
    selectedRowsPerPage,
    typeFilter,
    userOperationFilter,
    moduleId,
    startDate,
    endDate,
    userNameFilter
  ]);

  const handleDelete = async (e, deleteId) => {
      e.stopPropagation();

      await showConfirmation("simpleDelete", {
          entityName: "Log",
          entityId: deleteId,
          action: deleteMultipleLogsAction,
          onSuccess: (res) => {
              setSelectedRows([]);
              setCurrentPage(1);
              fetchFilteredLogsData();
          },
          onError: (error) => {
              setSnackbar({
                  open: true,
                  message: error?.response?.data?.message || "Error deleting log",
                  severity: "error",
              });
          },
          showCancelledMsg: true,
      });
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) {
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover these Log!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#22419e",
      cancelButtonColor: "#777777",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteMultipleLogsAction(selectedRows)
          .then((res) => {
            Swal.fire({
              title: "Deleted!",
              text: res.message,
              icon: "success",
              confirmButtonColor: "#22419e",
            });
          })
          .catch((error) => {
            setSnackbar({
              open: true,
              message: error.response?.data?.message || "Error deleting logs",
              severity: "error",
            });
          })
          .finally(() => {
            setSelectedRows([]);
            setCurrentPage(1);
            fetchFilteredLogsData();
          });
      } else {
        Swal.fire({
          title: "Cancelled",
          text: "Your record is safe",
          icon: "error",
          confirmButtonColor: "#22419e",
        });
      }
    });
  };

  const handleResetFilters = () => {
    const defaultRange = getDefaultDateRange();
    setLoading(true);
    setLogsData([]);
    setTypeFilter("all");
    setUserOperationFilter("all");
    setModuleId("all");
    setUserNameFilter(0);
    setStartDate(defaultRange.from.toISOString());
    setEndDate(defaultRange.to.toISOString());
    setSelectedRange(defaultRange);
    setSearchValue("");
    setCurrentPage(1);
    setResetButtonDisabled(true);
    setFilterButtonDisabled(true);
    setFiltersApplied(false);
  };

  const filters = [
    {
      key: "search",
      name: "search",
      type: "search",
      placeholder: "Search logs...",
    },
    {
      key: "userOperation",
      name: "userOperation",
      label: "Operation",
      type: "select",
      field: "type",
      options: [
        { label: "All Operations", value: "all" },
        { label: "Create", value: "Create" },
        { label: "Update", value: "Update" },
        { label: "Delete", value: "Delete" },
      ],
    },
    {
      key: "moduleId",
      name: "moduleId",
      label: "Module",
      type: "select",
      options: [
        { label: "All Modules", value: "all" },
        ...moduleOptions,
      ],
    },
    {
      key: "type",
      name: "type",
      label: "Type",
      type: "select",
      options: [
        { label: "All Types", value: "all" },
        { label: "Success", value: "success" },
        { label: "Error", value: "error" },
      ],
    },
    {
      key: "userId",
      name: "userId",
      label: "User",
      type: "select",
      options: [
        { label: "All Users", value: 0 },
        ...usersDataForDropdown.map((user) => ({
          value: user.id,
          label: `${JSON.parse(user.firstName)} ${JSON.parse(user.lastName)}`,
        })),
      ],
    },
    {
      key: "dateRange",
      name: "dateRange",
      label: "Date Range",
      type: "dateRange",
    },
  ];

  const filterValues = {
    search: searchValue,
    userOperation: userOperationFilter,
    moduleId,
    type: typeFilter,
    userId: userNameFilter,
    dateRange: selectedRange,
  };

  const handleApplyFilters = (values) => {
    setSearchValue(values.search || "");
    setUserOperationFilter(values.userOperation || "all");
    setModuleId(values.moduleId || "all");
    setTypeFilter(values.type || "all");
    setUserNameFilter(values.userId || 0);

    if (values.dateRange?.from) {
      const from = new Date(values.dateRange.from);
      setStartDate(new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0)).toISOString());
    }

    if (values.dateRange?.to) {
      const to = new Date(values.dateRange.to);
      setEndDate(new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999)).toISOString());
    }

    setCurrentPage(1);
  };

  const handleDataTableReset = () => {
    handleResetFilters();
  };

  const columns = [
    {
      field: "module",
      headerName: "Module Name",
      minWidth: 150,
      renderCell: ({ row }) =>
        row.moduleDetail?.moduleName || "-",
    },
    {
      field: "action",
      headerName: "User Operation",
      minWidth: 150,
      renderCell: ({ row }) => row.action || "-",
    },
    {
      field: "user",
      headerName: "User Name",
      minWidth: 150,
      renderCell: ({ row }) =>
        `${row?.user?.firstName || ""} ${row?.user?.lastName || ""}`,
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 150,
      renderCell: ({ row }) => {
        const statusMap = {
          success: {
            text: "Success",
            status: "success",
          },
          error: {
            text: "Error",
            status: "danger",
          },
        };

        const status =
          statusMap[row.type] ||
          {
            text: "Unknown",
            status: "default",
          };

        return renderStatusBadge({
          status: status.status,
          title: status.text,
        });
      },
    },
    {
      field: "ipAddress",
      headerName: "IP Address",
      minWidth: 150,
    },
    {
      field: "createdAt",
      headerName: "Date & Time",
      minWidth: 180,
      renderCell: ({ row }) =>
        formatDateTime(row.createdAt, true),
    },
  ];

  const actions = {
    delete: hasPermission?.canDelete,
  };

  const handleTableAction = (action, row) => {
    if (action === "delete") {
      handleDelete(
        {
          stopPropagation: () => { },
        },
        row.id
      );
    }
  };
  const bulkActions = {
    delete: hasPermission?.canDelete,
  };

  const handleBulkAction = (action, selectedIds) => {
    if (action === "delete") {
      handleDeleteSelected(selectedIds);
    }
  };

  return (
    <>
      <div id="kt_content_container">
        <BreadcrumbsComponent />
        <DataTable
          columns={columns}
          rows={logsData}
          loading={loading}
          totalCount={totalRecords}
          page={currentPage}
          pageSize={selectedRowsPerPage}
          filters={filters}
          filterValues={filterValues}
          actions={actions}
          bulkActions={bulkActions}
          onFilterApply={handleApplyFilters}
          onFilterReset={handleDataTableReset}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          onPageSizeChange={(size) => {
            setSelectedRowsPerPage(size);
            setCurrentPage(1);
          }}
          onAction={handleTableAction}
          onBulkAction={handleBulkAction}
          getRowId={(row) => row.id}
        />
        {openExportModal && (
          <ExportDataModal
            isOpen={openExportModal}
            onClose={() => {
              setOpenExportModal(false);
            }}
            blobData={exportData}
            data={selectedDataForExport}
            setSnackbar={setSnackbar}
          />
        )}
        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
        />
      </div>
    </>
  );
};

export default LogsSection;